/**
 * Drop-in replacement for the Base44 SDK client.
 *
 * The frontend used patterns like:
 *   base44.entities.Transaction.list('-date', 100, 0)
 *   base44.entities.Budget.create({...})
 *   base44.auth.me()
 *   base44.integrations.Core.InvokeLLM({ prompt, response_json_schema })
 *
 * To minimise churn we expose the *same* shape but back it with our own
 * REST API (see /backend). The transport lives in `apiClient.js`.
 */
import { apiClient } from './apiClient.js';

function entity(path) {
  return {
    /**
     * list(sort?, limit?, skip?)
     * Mirrors Base44's signature. Returns the array of rows directly.
     */
    list: (sort, limit, skip) =>
      apiClient.get(`/entities/${path}`, {
        params: { sort, limit, skip },
      }),

    /** Read a single record by id. */
    get: (id) => apiClient.get(`/entities/${path}/${id}`),

    /** Create one record. Returns the created row. */
    create: (data) => apiClient.post(`/entities/${path}`, data),

    /** Bulk insert. Returns { count }. */
    bulkCreate: (rows) => apiClient.post(`/entities/${path}/bulk`, rows),

    /** Partial update. Returns the updated row. */
    update: (id, data) => apiClient.patch(`/entities/${path}/${id}`, data),

    /** Delete by id. Returns nothing on success. */
    delete: (id) => apiClient.delete(`/entities/${path}/${id}`),
  };
}

export const base44 = {
  entities: {
    Transaction: entity('transactions'),
    Budget: entity('budgets'),
    BudgetOrder: entity('budget-order'),
    MonthlyIncome: entity('monthly-incomes'),
    SavingsGoal: entity('savings-goals'),
  },

  auth: {
    me: () => apiClient.get('/auth/me'),
    login: (email, password) =>
      apiClient.post('/auth/login', { email, password }).then((res) => {
        apiClient.setToken(res.token);
        return res;
      }),
    register: (data) =>
      apiClient.post('/auth/register', data).then((res) => {
        apiClient.setToken(res.token);
        return res;
      }),
    logout: () => {
      apiClient.setToken(null);
    },
    redirectToLogin: () => {
      apiClient.setToken(null);
      // SPA-internal: navigate to /login. Done via window.location so it
      // works even outside a router context.
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    },
  },

  // Replaces base44.integrations.Core.InvokeLLM
  integrations: {
    Core: {
      InvokeLLM: ({ prompt, response_json_schema }) =>
        apiClient.post('/llm/insights', { prompt, response_json_schema }),
    },
  },

  // Base44's app-logs feature is not part of self-hosted; provide a stub
  // so the existing NavigationTracker doesn't crash.
  appLogs: {
    logUserInApp: () => Promise.resolve(),
  },
};
